import { DEFAULT_PROPS } from './const';
import { TAG_PARTS } from './tagParts';
import type { TagStyleConfig } from './types';

const ACTION_BASE_STYLE = {
  borderRadius: 0,
  borderStyle: 'solid',
  borderColor: 'separator.border',
  height: '100%',
  padding: 0.25,
  flexShrink: 0,
  margin: 0,
  width: 'auto',
  '& svg': {
    padding: 'px',
  },
  _focusVisible: {
    boxShadow: 'focusInset',
  },
};

const CSS_SELECTORS = {
  action: {
    all: {
      focusVisible: ':has(button[data-action-type]:focus-visible)',
    },
    default: {
      hover: ':has(button[data-action-type="default"]:hover)',
      focusVisible: ':has(button[data-action-type="default"]:focus-visible)',
      active: ':has(button[data-action-type="default"]:active)',
      ariaExpanded:
        ':has(button[data-action-type="default"][aria-expanded="true"])',
    },
  },
  subAction: {
    default: {
      hover: ':has(button[data-sub-action-type="default"]:hover)',
      focusVisible:
        ':has(button[data-sub-action-type="default"]:focus-visible)',
      active: ':has(button[data-sub-action-type="default"]:active)',
      ariaExpanded:
        ':has(button[data-sub-action-type="default"][aria-expanded="true"])',
    },
    destructive: {
      hover: ':has(button[data-sub-action-type="destructive"]:hover)',
      focusVisible:
        ':has(button[data-sub-action-type="destructive"]:focus-visible)',
      active: ':has(button[data-sub-action-type="destructive"]:active)',
      ariaExpanded:
        ':has(button[data-sub-action-type="destructive"][aria-expanded="true"])',
    },
  },
} as const;

const BASE_STYLE = {
  container: {
    transitionProperty: 'common',
    transitionDuration: 'normal',
    overflow: 'clip',
    alignItems: 'stretch',
    backgroundColor: 'page.plateLight',
    color: 'feature.primary',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'separator.border',
    borderRadius: 'md',
    '& button.tag__label': {
      cursor: 'pointer',
    },
    [`&${CSS_SELECTORS.subAction.destructive.hover}, &${CSS_SELECTORS.subAction.destructive.focusVisible}`]:
      {
        borderColor: 'interaction.destructiveHover',
      },
    [`&${CSS_SELECTORS.subAction.destructive.active}, &${CSS_SELECTORS.subAction.destructive.ariaExpanded}`]:
      {
        borderColor: 'interaction.destructiveActive',
      },
    [`&${CSS_SELECTORS.subAction.default.hover}, &${CSS_SELECTORS.subAction.default.focusVisible}`]:
      {
        borderColor: 'interaction.hover',
      },
    [`&${CSS_SELECTORS.subAction.default.active}, &${CSS_SELECTORS.subAction.default.ariaExpanded}`]:
      {
        borderColor: 'interaction.active',
      },
    [CSS_SELECTORS.action.default.hover]: {
      backgroundColor: 'interaction.surfaceHover',
      color: 'interaction.hover',
      borderColor: 'interaction.hover',
      '& .tag__icon': {
        color: 'inherit',
      },
    },
    [CSS_SELECTORS.action.default.focusVisible]: {
      borderColor: 'interaction.hover',
    },
    [`&${CSS_SELECTORS.action.default.active}, &${CSS_SELECTORS.action.default.ariaExpanded}`]:
      {
        [[
          `:not(${CSS_SELECTORS.subAction.destructive.hover})`,
          `:not(${CSS_SELECTORS.subAction.destructive.focusVisible})`,
          `:not(${CSS_SELECTORS.subAction.destructive.active})`,
          `:not(${CSS_SELECTORS.subAction.destructive.ariaExpanded})`,
        ].join('')]: {
          borderColor: 'interaction.hover',
        },
        backgroundColor: 'interaction.surfaceActive',
        color: 'interaction.active',
        '& .tag__icon': {
          color: 'inherit',
        },
      },
    [CSS_SELECTORS.action.all.focusVisible]: {
      boxShadow: 'focus',
    },
  },
  label: {
    display: 'inline-flex',
    maxWidth: 45,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: 'default',
    fontSize: 'sm',
    fontWeight: 'medium',
    px: 0.75,
    _focusVisible: {
      outline: 'none',
    },
  },
  leftAction: {
    ...ACTION_BASE_STYLE,
    paddingRight: 0.5,
    borderRightWidth: '1px',
  },
  rightAction: {
    ...ACTION_BASE_STYLE,
    paddingLeft: 0.5,
    borderLeftWidth: '1px',
  },
};

const SIZES: TagStyleConfig['sizes'] = {
  xs: {
    container: {
      minHeight: 2.5,
    },
    label: {
      lineHeight: '1.125rem',
    },
    icon: {
      boxSize: 1.75,
    },
  },
  sm: {
    container: {
      minHeight: 3,
    },
    label: {
      lineHeight: '1.35rem',
    },
    icon: {
      boxSize: 2.25,
    },
  },
};

export const TAG_STYLE_CONFIG: TagStyleConfig = {
  parts: TAG_PARTS,
  baseStyle: BASE_STYLE,
  sizes: SIZES,
  defaultProps: DEFAULT_PROPS,
};
